import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Spinner, Alert, Modal, ModalHeader, ModalBody, ModalFooter
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const getAuthHeaders = () => {
  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  return {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  };
};

const YorumYonetimi = () => {
  document.title = "Yorum Yönetimi | HarmanaGEL";

  const [yorumlar, setYorumlar] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [detailModal, setDetailModal] = useState(false);
  const [selectedYorum, setSelectedYorum] = useState(null);

  const fetchYorumlar = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${BASE_URL}/YORUMLAR?$top=500&$orderby=YORUM_TARIHI desc&$expand=KULLANICILAR($select=AD_SOYAD,Mail_Adress),KULLANICILAR1($select=AD_SOYAD),ILANLAR($select=AD)`,
        { headers: getAuthHeaders() }
      );
      const result = await response.json();
      if (result && result.value) setYorumlar(result.value);
    } catch (err) {
      setError("Yorumlar yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchYorumlar();
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Bu yorumu silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/YORUMLAR(${id})`, { method: "DELETE", headers: getAuthHeaders() });
      setSuccessMsg("Yorum silindi.");
      fetchYorumlar();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const puanBadge = (puan) => {
    const p = parseFloat(puan || 0);
    let color = "danger";
    if (p >= 4) color = "success";
    else if (p >= 3) color = "warning";
    else if (p >= 2) color = "info";
    return <Badge color={color} className="fs-6">{p.toFixed(1)}</Badge>;
  };

  const handleViewDetail = (yorum) => {
    setSelectedYorum(yorum);
    setDetailModal(true);
  };

  const columns = useMemo(() => [
    {
      header: "ID",
      accessorKey: "ID",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => <span className="fw-bold">#{cell.getValue()}</span>,
    },
    {
      header: "Yorum Yapan",
      accessorKey: "YORUM_YAPAN",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? (
          <div>
            <div className="fw-medium">{u.AD_SOYAD}</div>
            <small className="text-muted">{u.Mail_Adress}</small>
          </div>
        ) : <span className="text-muted">Kullanıcı #{cell.row.original.YORUM_YAPAN_ID}</span>;
      },
    },
    {
      header: "Satıcı",
      accessorKey: "SATICI",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const u = cell.getValue();
        return u ? <span className="fw-medium">{u.AD_SOYAD}</span> : <span className="text-muted">Kullanıcı #{cell.row.original.SATICI_ID}</span>;
      },
    },
    {
      header: "İlan",
      accessorKey: "ILAN",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const i = cell.getValue();
        return i ? <span className="fw-medium">{i.AD}</span> : <span className="text-muted">İlan #{cell.row.original.ILANLAR_ID || "-"}</span>;
      },
    },
    {
      header: "Puan",
      accessorKey: "PUAN",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => puanBadge(cell.getValue()),
    },
    {
      header: "Yorum Metni",
      accessorKey: "YORUM_METNI",
      enableSorting: false,
      enableColumnFilter: true,
      cell: (cell) => {
        const text = cell.getValue() || "";
        return <span className="text-muted" style={{ maxWidth: 250, display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{text}</span>;
      },
    },
    {
      header: "Tarih",
      accessorKey: "YORUM_TARIHI",
      enableSorting: true,
      enableColumnFilter: false,
      cell: (cell) => {
        const d = cell.getValue();
        return d ? new Date(d).toLocaleDateString("tr-TR") : "-";
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      enableColumnFilter: false,
      cell: (cellProps) => {
        const y = cellProps.row.original;
        return (
          <div className="d-flex gap-1">
            <Button color="info" size="sm" outline onClick={() => handleViewDetail(y)}>
              <i className="bx bx-detail" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(y.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="HarmanaGEL" breadcrumbItem="Yorum Yönetimi" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h4 className="card-title mb-0">Yorumlar</h4>
                    <Button color="primary" size="sm" onClick={fetchYorumlar} disabled={isLoading}>
                      <i className="bx bx-refresh me-1" />
                      Yenile
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={yorumlar}
                      isGlobalFilter={true}
                      SearchPlaceholder="Yorum ara..."
                      isPagination={true}
                      isCustomPageSize={true}
                      tableClass="table-bordered align-middle nowrap w-100 mb-0"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={detailModal} toggle={() => setDetailModal(false)} size="lg" centered>
        <ModalHeader toggle={() => setDetailModal(false)}>
          Yorum Detayı
        </ModalHeader>
        <ModalBody>
          {selectedYorum && (
            <>
              <Row className="mb-3">
                <Col md={6}>
                  <p><strong>Yorum Yapan:</strong> {selectedYorum.KULLANICILAR?.AD_SOYAD || "-"}</p>
                  <p><strong>Satıcı:</strong> {selectedYorum.KULLANICILAR1?.AD_SOYAD || "-"}</p>
                  <p><strong>İlan:</strong> {selectedYorum.ILANLAR?.AD || "-"}</p>
                </Col>
                <Col md={6}>
                  <p><strong>Puan:</strong> {puanBadge(selectedYorum.PUAN)}</p>
                  <p><strong>Tarih:</strong> {selectedYorum.YORUM_TARIHI ? new Date(selectedYorum.YORUM_TARIHI).toLocaleString("tr-TR") : "-"}</p>
                </Col>
              </Row>
              <div className="p-3 bg-light rounded">
                <p className="mb-0">{selectedYorum.YORUM_METNI || "Yorum metni yok."}</p>
              </div>
            </>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setDetailModal(false)}>Kapat</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default YorumYonetimi;
